import type { GetStaticPropsContext } from 'next';

import { PageCategory } from '~/features/analytics/constants';
import {
  StaticPartnershipsSectionProps,
  transformStaticPartnershipsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/partnerships-section';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { PlansPage } from '~/features/plans/components/plans-page';
import { getSubscriptionSeoPriceData } from '~/features/seo/controllers/get-subscription-seo-price-data';
import {
  createSubscriptionStructuredData,
  serializeSubscriptionStructuredData,
} from '~/features/seo/utils/create-subscription-structured-data';
import { getAllThirdPartyReviews } from '~/features/third-party-reviews/controller';
import { ThirdPartyReviewContentList } from '~/features/third-party-reviews/model';

const PAGE_URL = 'https://www.udacity.com/plans';

export default function Plans({
  partnershipsSectionProps,
  page,
  thirdPartyReviews,
  structuredData,
}: {
  page: Page;
  partnershipsSectionProps: StaticPartnershipsSectionProps;
  thirdPartyReviews: ThirdPartyReviewContentList;
  structuredData: string;
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Plans} />
      <PageLayout withoutVerticalPadding>
        <PlansPage partnershipsSectionProps={partnershipsSectionProps} thirdPartyReviews={thirdPartyReviews} />
      </PageLayout>
      <script dangerouslySetInnerHTML={{ __html: structuredData }} type='application/ld+json' />
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, partnershipsSectionProps, thirdPartyReviews, priceData] = await Promise.all([
    getPage('/plans'),
    transformStaticPartnershipsSectionProps(),
    getAllThirdPartyReviews(),
    getSubscriptionSeoPriceData(),
  ]);

  const structuredData = serializeSubscriptionStructuredData(
    createSubscriptionStructuredData({
      url: PAGE_URL,
      name: 'Udacity Subscription',
      description: page.seo.description,
      breadcrumbName: 'Plans',
      priceData,
      includeTeam: true,
    })
  );

  return {
    props: {
      page,
      partnershipsSectionProps,
      thirdPartyReviews,
      structuredData,
    },
  };
}
